import * as z from "zod";
import type { Transaction } from "@rocicorp/zero";
import { type Schema, zql } from "../generatedDrizzleSchema";
import { assertAccess } from "../rbac/access";
import { mutator } from "./mutator";

export const deleteBranch = mutator({
  schema: z.object({ branchId: z.string() }),
  resolve: {
    branch: { kind: "branch", from: "branchId" },
  },
  // The required permission depends on runtime state the declarative gate can't
  // see, so it's asserted in fn. The manage gate protects live workflows only:
  // deleting `main` takes the live version out of operation (space:push_live),
  // or — when it's the only branch — destroys a live workflow, which takes
  // space:manage like permanentlyDeleteWorkflow. Deleting any non-main branch is
  // a plain draft edit (space:write), even when it's the workflow's only branch:
  // a never-published draft workflow is scratch an editor can already archive.
  permissions: {
    none: "permission depends on branch liveness/count, asserted in fn",
  },
  fn: async ({ tx, ctx, args: { branch } }) => {
    const branches = await tx.run(
      zql.workflowBranches.where("workflowId", branch.workflowId)
    );
    const isOnlyBranch = branches.length === 1;
    const permission =
      branch.name === "main"
        ? isOnlyBranch
          ? "space:manage"
          : "space:push_live"
        : "space:write";
    await assertAccess({
      tx,
      userId: ctx.userId,
      principal: ctx.principal,
      scopeId: branch.workflow.spaceId,
      tenantId: ctx.tenantId,
      permission,
    });
    await deleteBranchInTransaction(tx, branch);
  },
});

export async function deleteBranchInTransaction(
  tx: Transaction<Schema>,
  branch: { id: string; workflowId: string }
) {
  // Clear autofixBranchId/autoImproveBranchId if this was that AI branch
  const [workflow] = await tx.run(zql.workflows.where("id", branch.workflowId));
  if (workflow?.autofixBranchId === branch.id) {
    await tx.mutate.workflows.update({
      id: workflow.id,
      autofixBranchId: null,
    });
  }
  if (workflow?.autoImproveBranchId === branch.id) {
    await tx.mutate.workflows.update({
      id: workflow.id,
      autoImproveBranchId: null,
    });
  }

  // Cascades will handle commits, history items, and chats
  await tx.mutate.workflowBranches.delete({ id: branch.id });

  const remaining = await tx.run(
    zql.workflowBranches.where("workflowId", branch.workflowId)
  );
  if (remaining.length === 0) {
    await tx.mutate.workflows.delete({ id: branch.workflowId });
  }
}
